import { Link, useLocation } from 'react-router-dom';
import { UserIcon, BuildingOfficeIcon } from '@heroicons/react/24/outline';
import { useAuth } from "../../context/AuthContext/AuthContext";

import { usePageContext } from '../../context/PageContext/PageContext';
import { mergeNavForRoles, FOOTER_RULES } from '../../utils/roleNavMatrix';
import './DashboardSideNav.css';

const DashboardSideNav = ({ roles }) => {
    const location = useLocation();
    const { profile, isAuthLoading } = useAuth();
    const { mobileSidebarOpen, setMobileSidebarOpen, showDesktopSideNav } = usePageContext();

    const isLinkActive = (href) => location.pathname === href;

    // Merge nav items for all roles, deduplicating by href
    let navItems = mergeNavForRoles(roles);

    if (navItems) {
        navItems = navItems.map((item) => ({
            ...item,
            current: isLinkActive(item.href),
        }));
    }

    let mainSideNavClasses = `parkpro-side-nav dashboard-side-nav fixed top-[100px] z-30 lg:z-auto lg:static transition-transform duration-300 ${mobileSidebarOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0 max-w-72 w-10/12 h-[calc(100vh_-_100px)] lg:w-72 lg:h-full px-6 py-4 overflow-y-auto`;

    if (showDesktopSideNav === false) {
        mainSideNavClasses = `parkpro-side-nav dashboard-side-nav fixed top-[100px] z-30 transition-transform duration-300 ${mobileSidebarOpen ? 'translate-x-0' : '-translate-x-full'} max-w-72 w-10/12 h-[calc(100vh_-_100px)] px-6 py-4 overflow-y-auto`;
    }

    // Signed-in users get the phone menu panel (MobileNav) below 1024 px, so
    // this slide-in only serves signed-out visitors there. At 1024 px and
    // wider nothing changes.
    const signedIn = Boolean(profile);
    if (signedIn) {
        mainSideNavClasses = `${mainSideNavClasses} max-lg:hidden`;
    }

    return (
        <>
            <div className={mainSideNavClasses}>
                <nav className="flex flex-1 flex-col pt-6">
                    {isAuthLoading || !navItems ? (
                        <>
                            <div className="animate-pulse flex mt-4">
                                <div className="h-6 bg-gray-500/15 rounded-md w-full"></div>
                            </div>
                            <div className="animate-pulse flex mt-4">
                                <div className="h-6 bg-gray-500/15 rounded-md w-full"></div>
                            </div>
                            <div className="animate-pulse flex mt-4">
                                <div className="h-6 bg-gray-500/15 rounded-md w-full"></div>
                            </div>
                            <div className="animate-pulse flex mt-4">
                                <div className="h-6 bg-gray-500/15 rounded-md w-full"></div>
                            </div>
                            <div className="animate-pulse flex mt-4">
                                <div className="h-6 bg-gray-500/15 rounded-md w-full"></div>
                            </div>
                            <div className="animate-pulse flex mt-4">
                                <div className="h-6 bg-gray-500/15 rounded-md w-full"></div>
                            </div>
                        </>
                    ) : (
                        <div className="w-full h-full lg:flex lg:flex-1 lg:flex-col lg:mt-4">
                            <ul role="list" className="side-nav-list flex flex-1 flex-col">
                                <li>
                                    <ul role="list" className="side-nav-list">
                                        {navItems.map((item, index) => (
                                            <li key={item.name || `separator-${index}`}>
                                                {item.type === 'separator' ? (
                                                    <div className="side-nav-separator" />
                                                ) : (
                                                    <Link
                                                        to={item.href}
                                                        className={`side-nav-link${item.current ? ' active' : ''}`}
                                                    >
                                                        <item.icon
                                                            className="side-nav-icon"
                                                            aria-hidden="true"
                                                        />
                                                        {item.name}
                                                    </Link>
                                                )}
                                            </li>
                                        ))}
                                    </ul>
                                </li>
                                <li className="side-nav-footer">
                                    <ul className="side-nav-list">
                                        { profile ? (
                                            <>
                                            <li>
                                                <Link
                                                    to="/profile"
                                                    className={`side-nav-link${isLinkActive('/profile') ? ' active' : ''}`}
                                                >
                                                    <UserIcon
                                                        className="side-nav-icon"
                                                        aria-hidden="true"
                                                    />
                                                    My Profile
                                                </Link>
                                            </li>
                                            {(roles ?? []).some((r) => FOOTER_RULES.showCompanySettingsRoles.has(r)) && (
                                            <li>
                                                <Link
                                                    to="/company"
                                                    className={`side-nav-link${isLinkActive('/company') ? ' active' : ''}`}
                                                >
                                                    <BuildingOfficeIcon
                                                        className="side-nav-icon"
                                                        aria-hidden="true"
                                                    />
                                                    Company Settings
                                                </Link>
                                            </li>
                                            )}
                                            </>
                                        ) : (
                                            <>
                                                <li>
                                                    <Link
                                                        to="/login"
                                                        className="side-nav-link"
                                                    >
                                                        Sign In
                                                    </Link>
                                                </li>
                                                <li>
                                                    <Link
                                                        to="/login"
                                                        className="side-nav-link"
                                                    >
                                                        Sign Up For Free
                                                    </Link>
                                                </li>
                                            </>
                                        )}
                                    </ul>
                                </li>
                            </ul>
                        </div>
                    )}
                </nav>
            </div>
            {mobileSidebarOpen && !signedIn && (
                <div className="fixed h-[calc(100vh_-_100px)] w-full left-0 top-[100px] bg-black bg-opacity-50 z-20 lg:hidden" onClick={() => setMobileSidebarOpen(false)} />
            )}
        </>
    );
};

export default DashboardSideNav;